"use client"

import { Badge } from "@tokenterminal/core/ui/messaging/badge/Badge"
import { cn } from "@tokenterminal/core/utils/cn"
import Image from "next/image"
import { type ReactNode, useCallback, useEffect, useRef, useState } from "react"

interface StatItem {
  value: string | number | ReactNode
  label: string
  logos?: Array<{ src: string; alt: string }>
  hasSparkline?: boolean
  wide?: boolean
  badge?: string
}

interface StatsGridProps {
  sectionLabel?: string
  stats: [StatItem, StatItem, StatItem, StatItem]
}

/**
 * Parses a string like "1,200+" or "2+ PB" into { number, prefix, suffix }
 * Returns null if the value isn't a countable string.
 */
function parseCountableValue(value: string | number | ReactNode): {
  number: number
  prefix: string
  suffix: string
} | null {
  if (typeof value !== "string") return null
  const match = value.match(/^([^\d]*?)([\d,]+)(.*)$/)
  if (!match) return null
  const num = Number.parseInt(match[2].replace(/,/g, ""), 10)
  if (Number.isNaN(num)) return null
  return { number: num, prefix: match[1], suffix: match[3] }
}

function formatNumber(n: number): string {
  return n.toLocaleString("en-US")
}

function AnimatedValue({ value }: { value: string | number | ReactNode }) {
  const parsed = parseCountableValue(value)
  const [count, setCount] = useState(0)
  const [hasAnimated, setHasAnimated] = useState(false)
  const ref = useRef<HTMLSpanElement>(null)

  const animate = useCallback(() => {
    if (!parsed || hasAnimated) return
    setHasAnimated(true)

    const target = parsed.number
    const duration = 1200
    const startTime = performance.now()

    function tick(now: number) {
      const elapsed = now - startTime
      const progress = Math.min(elapsed / duration, 1)
      // Ease-out cubic
      const eased = 1 - (1 - progress) ** 3
      setCount(Math.round(eased * target))
      if (progress < 1) {
        requestAnimationFrame(tick)
      }
    }

    requestAnimationFrame(tick)
  }, [parsed, hasAnimated])

  useEffect(() => {
    if (!parsed || !ref.current) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          animate()
          observer.disconnect()
        }
      },
      { threshold: 0.3 }
    )

    observer.observe(ref.current)
    return () => observer.disconnect()
  }, [parsed, animate])

  if (!parsed) {
    return <>{value}</>
  }

  return (
    <span ref={ref}>
      {parsed.prefix}
      {formatNumber(count)}
      {parsed.suffix}
    </span>
  )
}

export function StatsGrid({
  sectionLabel = "By the numbers",
  stats,
}: StatsGridProps) {
  return (
    <div className="max-w-6xl mx-auto">
      <div className="max-w-5xl mx-auto">
        {sectionLabel && (
          <div className="mb-6">
            <p className="font-mono text-xs uppercase text-fg-secondary tracking-[1px]">
              {sectionLabel}
            </p>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 border-t border-l border-dashed border-border-default">
          {stats.map((stat, index) => (
            <div
              key={stat.label}
              className={cn(
                "relative flex flex-col gap-4 p-8 border-r border-b border-dashed border-border-default overflow-hidden",
                stat.wide && "lg:col-span-2"
              )}
            >
              {stat.hasSparkline && (
                <div className="absolute inset-0 opacity-20 pointer-events-none">
                  <svg
                    width="100%"
                    height="100%"
                    viewBox="0 0 200 100"
                    preserveAspectRatio="none"
                    className="absolute inset-0"
                  >
                    <defs>
                      <linearGradient
                        id={`sparklineGradient-${index}`}
                        x1="0%"
                        y1="0%"
                        x2="0%"
                        y2="100%"
                      >
                        <stop
                          offset="0%"
                          stopColor="rgb(20, 184, 166)"
                          stopOpacity="0.3"
                        />
                        <stop
                          offset="100%"
                          stopColor="rgb(20, 184, 166)"
                          stopOpacity="0"
                        />
                      </linearGradient>
                    </defs>
                    <path
                      d="M 0,80 Q 25,60 50,50 T 100,40 T 150,30 T 200,20 L 200,100 L 0,100 Z"
                      fill={`url(#sparklineGradient-${index})`}
                    />
                    <path
                      d="M 0,80 Q 25,60 50,50 T 100,40 T 150,30 T 200,20"
                      stroke="rgb(20, 184, 166)"
                      strokeWidth="2"
                      fill="none"
                    />
                  </svg>
                </div>
              )}
              <div className="text-4xl lg:text-5xl font-medium tracking-tighter leading-none relative z-10">
                <AnimatedValue value={stat.value} />
              </div>
              <div className="flex items-center gap-2 text-sm text-fg-secondary relative z-10 whitespace-nowrap">
                <span>{stat.label}</span>
                {stat.badge && (
                  <Badge color="green" variant="solid" size="small">
                    {stat.badge}
                  </Badge>
                )}
              </div>
              {stat.logos && stat.logos.length > 0 && (
                <div className="flex items-center mt-2">
                  {stat.logos.map((logo, logoIndex) => (
                    <Image
                      key={logo.alt}
                      src={logo.src}
                      alt={logo.alt}
                      width={24}
                      height={24}
                      className={cn(
                        "w-6 h-6 rounded-full object-cover border-2 border-bg-default",
                        logoIndex > 0 && "-ml-2"
                      )}
                      style={{ zIndex: 50 - logoIndex * 10 }}
                    />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
